import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, X, Sparkles } from 'lucide-react';
import api, { absoluteAsset } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import ImageUpload from '../../components/ImageUpload';
import { useToast } from '../../hooks/use-toast';

const emptyForm = { id: '', title: '', description: '', image: '', brandId: '__none__', featured: false };

const PostsAdmin = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([api.get('/posts'), api.get('/brands')]);
      setPosts(p.data);
      setBrands(b.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, brandId: p.brandId || '__none__' }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast({ title: 'El título es obligatorio' });
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || '',
        image: form.image || '',
        brandId: form.brandId === '__none__' ? null : form.brandId,
        featured: !!form.featured,
      };
      if (form.id) await api.put(`/posts/${form.id}`, payload);
      else await api.post('/posts', payload);
      toast({ title: form.id ? 'Post actualizado' : 'Post publicado' });
      setOpen(false);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo guardar' });
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/posts/${deleting.id}`);
      toast({ title: 'Post eliminado' });
      setDeleting(null);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || '' });
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('es-PA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch (_e) { return iso; }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Lo Nuevo</h2>
          <p className="text-sm text-slate-500">{posts.length} publicaciones</p>
        </div>
        <Button onClick={openNew} className="h-10 gap-2 bg-emerald-700 text-white hover:bg-emerald-800">
          <Plus className="h-4 w-4" /> Nueva publicación
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-700" /></div>
      ) : posts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 py-16 text-center text-slate-500">
          Aún no hay publicaciones. Crea la primera para anunciar productos nuevos.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {posts.map(p => (
            <div key={p.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex">
                <div className="flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden bg-slate-50">
                  {p.image ? (
                    <img src={absoluteAsset(p.image)} alt={p.title} className="h-full w-full object-contain p-2" />
                  ) : (
                    <Sparkles className="h-7 w-7 text-slate-300" />
                  )}
                </div>
                <div className="min-w-0 flex-1 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-slate-900">{p.title}</h3>
                      <p className="mt-0.5 text-xs text-slate-500">{fmtDate(p.createdAt)}</p>
                    </div>
                    {p.featured && (
                      <Badge className="shrink-0 bg-emerald-600 text-white hover:bg-emerald-600">Destacado</Badge>
                    )}
                  </div>
                  {p.brandName && (
                    <Badge variant="secondary" className="mt-2 bg-emerald-50 text-emerald-800 hover:bg-emerald-50">
                      {p.brandName}
                    </Badge>
                  )}
                  {p.description && (
                    <p className="mt-2 line-clamp-2 text-xs text-slate-600">{p.description}</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="h-8 gap-1 border-slate-300">
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDeleting(p)} className="h-8 gap-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                  <Trash2 className="h-3.5 w-3.5" /> Eliminar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Editar publicación' : 'Nueva publicación'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="¡Llegó el nuevo producto X!" className="h-11" required />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Cuenta a tus clientes sobre el nuevo producto, sus beneficios, presentación, etc."
                rows={5}
              />
            </div>
            <div className="space-y-2">
              <Label>Marca asociada (opcional)</Label>
              <Select value={form.brandId} onValueChange={(v) => setForm({ ...form, brandId: v })}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Sin marca" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin marca asociada</SelectItem>
                  {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <ImageUpload label="Imagen del post (recomendado cuadrada)" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-slate-800">Destacar como “Nuevo”</p>
                <p className="text-xs text-slate-500">Muestra una etiqueta destacada y aparece primero en el feed.</p>
              </div>
              <Switch checked={!!form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="gap-1"><X className="h-4 w-4" /> Cancelar</Button>
              <Button type="submit" disabled={saving} className="gap-2 bg-emerald-700 text-white hover:bg-emerald-800">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Publicar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar publicación?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleting?.title}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PostsAdmin;
